import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { works, categories } from '@/mocks/works';
import WorkCard from '@/components/feature/WorkCard';
import Reveal from '@/components/base/Reveal';

const sortOptions = [
  { id: 'heat', label: '按热度', icon: 'ri-fire-line' },
  { id: 'latest', label: '按最新', icon: 'ri-time-line' },
  { id: 'views', label: '按围观', icon: 'ri-eye-line' },
];

const PAGE_SIZE = 6;

export default function WorksExplorer() {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get('category') || '全部';
  const queryParam = searchParams.get('q') || '';
  const [sort, setSort] = useState('heat');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [keyword, setKeyword] = useState(queryParam);

  const tabs = ['全部', ...categories.map((c) => c.name)];

  useEffect(() => {
    setKeyword(queryParam);
  }, [queryParam]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [categoryParam, queryParam, sort]);

  const filtered = useMemo(() => {
    let list = [...works];
    if (categoryParam !== '全部') {
      list = list.filter((w) => w.category === categoryParam);
    }
    const q = queryParam.trim();
    if (q) {
      list = list.filter((w) => w.title.includes(q) || w.summary.includes(q) || w.contestant.includes(q));
    }
    if (sort === 'heat') {
      list.sort((a, b) => b.heat - a.heat);
    } else if (sort === 'latest') {
      list.sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
    } else {
      list.sort((a, b) => b.views - a.views);
    }
    return list;
  }, [categoryParam, queryParam, sort]);

  const shown = filtered.slice(0, visible);

  const applyCategory = (name: string) => {
    const next: Record<string, string> = {};
    if (name !== '全部') next.category = name;
    if (queryParam) next.q = queryParam;
    setSearchParams(next);
  };

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (categoryParam !== '全部') next.category = categoryParam;
    const trimmed = keyword.trim();
    if (trimmed) next.q = trimmed;
    setSearchParams(next);
  };

  return (
    <div className="w-full">
      <form
        onSubmit={handleSearch}
        className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-4 md:flex-row md:items-center md:p-5"
        role="search"
      >
        <div className="relative flex-1">
          <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
          <input
            type="search"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索作品标题、简介或选手昵称"
            aria-label="搜索参赛作品"
            className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 font-body text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
        <button
          type="submit"
          className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-search-line"></i>
          搜索作品
        </button>
      </form>

      <div className="mt-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          {tabs.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => applyCategory(name)}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 font-label text-sm transition-colors ${
                categoryParam === name
                  ? 'bg-primary-500 text-background-50'
                  : 'border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700'
              }`}
            >
              {name}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
          {sortOptions.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => setSort(option.id)}
              className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 font-label text-sm transition-colors ${
                sort === option.id ? 'bg-background-50 text-foreground-950' : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              <i className={option.icon}></i>
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <p className="mt-5 font-label text-xs text-foreground-500">
        共找到 <span className="font-bold text-primary-600">{filtered.length}</span> 篇作品
        {categoryParam !== '全部' && ` · 主题「${categoryParam}」`}
        {queryParam && ` · 关键词「${queryParam}」`}
      </p>

      {shown.length > 0 ? (
        <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((work, index) => (
            <Reveal key={work.id} delay={(index % 3) * 70}>
              <WorkCard work={work} />
            </Reveal>
          ))}
        </div>
      ) : (
        <div className="mt-10 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
          <i className="ri-inbox-line text-3xl text-foreground-400"></i>
          <p className="mt-3 font-heading text-base font-bold text-foreground-800">没有找到符合条件的作品</p>
          <p className="mt-1 font-body text-sm text-foreground-500">换个主题或关键词再试试看</p>
        </div>
      )}

      {visible < filtered.length && (
        <div className="mt-10 flex justify-center">
          <button
            type="button"
            onClick={() => setVisible((v) => v + PAGE_SIZE)}
            className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-6 py-3 font-label text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
          >
            <i className="ri-add-line"></i>
            加载更多作品（剩余 {filtered.length - visible} 篇）
          </button>
        </div>
      )}
    </div>
  );
}